import { useState, useEffect } from "react";
import { 
  Card, 
  CardContent, 
  Typography, 
  Button, 
  Chip
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../services/api";
import MainCarousel from "../MainCarousel/MainCarousel";
import { 
  HiSparkles, 
  HiArrowLeft, 
  HiCurrencyRupee, 
  HiCog, 
  HiBattery100, 
  HiWrenchScrewdriver,
  HiBolt,
  HiChartBar,
  HiInformationCircle,
  HiEye,
  HiHeart
} from "react-icons/hi2";

// Tab configuration
const tabs = [
  { id: 0, label: "Primary Details", icon: <HiInformationCircle className="w-5 h-5" /> },
  { id: 1, label: "Specifications", icon: <HiCog className="w-5 h-5" /> },
  { id: 2, label: "Servicing", icon: <HiWrenchScrewdriver className="w-5 h-5" /> },
  { id: 3, label: "Battery Info", icon: <HiBattery100 className="w-5 h-5" /> }
];

export default function NewBikeDetailShort() {
  const [newBike, setNewBike] = useState();
  const [images, setImages] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const bikeId = new URLSearchParams(location.search).get("bikeId");

  useEffect(() => {
    if (bikeId) {
      setLoading(true);
      api.get("/public/newBikes/" + bikeId).then((res) => {
        setNewBike(res.data);
        setImages(res.data.images || []);
        setLoading(false);
      }).catch(() => {
        setLoading(false);
      });
    }
  }, [bikeId]);

  const renderTabContent = () => {
    if (!newBike) return null;

    switch (activeTab) {
      case 0:
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-6"
          >
            {/* Primary Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="bg-white rounded-2xl shadow-lg border-0" sx={{ borderRadius: "16px" }}>
                <CardContent className="p-6">
                  <div className="flex items-center mb-4">
                    <div className="p-2 bg-primary-100 rounded-lg mr-3">
                      <HiSparkles className="w-6 h-6 text-primary-600" />
                    </div>
                    <Typography variant="h6" className="font-bold text-gray-900" sx={{ fontSize: "1.25rem" }}>
                      Basic Information
                    </Typography>
                  </div>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-gray-600 font-medium">Brand</span>
                      <span className="font-semibold text-gray-900">{newBike.brand}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-gray-600 font-medium">Model</span>
                      <span className="font-semibold text-gray-900">{newBike.model}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-gray-600 font-medium">Color</span>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded-full bg-gray-300"></div>
                        <span className="font-semibold text-gray-900">{newBike.color}</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-gray-600 font-medium">Variant</span>
                      <span className="font-semibold text-gray-900">{newBike.variant}</span>
                    </div>
                    {newBike.mileage && (
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-gray-600 font-medium">Mileage</span>
                        <span className="font-semibold text-gray-900">{newBike.mileage}</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {newBike.fuelType === "ELECTRIC" && (
                <Card className="bg-white rounded-2xl shadow-lg border-0" sx={{ borderRadius: "16px" }}>
                  <CardContent className="p-6">
                    <div className="flex items-center mb-4">
                      <div className="p-2 bg-green-100 rounded-lg mr-3">
                        <HiBattery100 className="w-6 h-6 text-green-600" />
                      </div>
                      <Typography variant="h6" className="font-bold text-gray-900" sx={{ fontSize: "1.25rem" }}>
                        Electric Features
                      </Typography>
                    </div>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-gray-600 font-medium">Range</span>
                        <span className="font-semibold text-gray-900">{newBike.rangeInKm} kms</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-gray-600 font-medium">Top Speed</span>
                        <span className="font-semibold text-gray-900">{newBike.topSpeedInKmph} Km/Hr</span>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-gray-600 font-medium">Fuel Type</span>
                        <Chip label="Electric" className="bg-green-100 text-green-800" size="small" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* About Section */}
            <Card className="bg-white rounded-2xl shadow-lg border-0" sx={{ borderRadius: "16px" }}>
              <CardContent className="p-6">
                <div className="flex items-center mb-4">
                  <div className="p-2 bg-blue-100 rounded-lg mr-3">
                    <HiInformationCircle className="w-6 h-6 text-blue-600" />
                  </div>
                  <Typography variant="h6" className="font-bold text-gray-900" sx={{ fontSize: "1.25rem" }}>
                    About {newBike.brand} {newBike.model}
                  </Typography>
                </div>
                <Typography variant="body1" className="text-gray-600 leading-relaxed" sx={{ fontSize: "1rem" }}>
                  {newBike.summary}
                </Typography>
              </CardContent>
            </Card>
          </motion.div>
        );

      case 1:
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="bg-white rounded-2xl shadow-lg border-0" sx={{ borderRadius: "16px" }}>
                <CardContent className="p-6">
                  <div className="flex items-center mb-4">
                    <div className="p-2 bg-purple-100 rounded-lg mr-3">
                      <HiCog className="w-6 h-6 text-purple-600" />
                    </div>
                    <Typography variant="h6" className="font-bold text-gray-900" sx={{ fontSize: "1.25rem" }}>
                      Engine & Performance
                    </Typography>
                  </div>
                  <div className="space-y-4">
                    {newBike.engineCapacity && (
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-gray-600 font-medium">Engine Capacity</span>
                        <span className="font-semibold text-gray-900">{newBike.engineCapacity}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-gray-600 font-medium">Power</span>
                      <span className="font-semibold text-gray-900">{newBike.power}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-gray-600 font-medium">Torque</span>
                      <span className="font-semibold text-gray-900">{newBike.torque}</span>
                    </div>
                    {newBike.fuelCapacity && (
                      <div className="flex justify-between items-center py-2">
                        <span className="text-gray-600 font-medium">Fuel Capacity</span>
                        <span className="font-semibold text-gray-900">{newBike.fuelCapacity}</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white rounded-2xl shadow-lg border-0" sx={{ borderRadius: "16px" }}>
                <CardContent className="p-6">
                  <div className="flex items-center mb-4">
                    <div className="p-2 bg-orange-100 rounded-lg mr-3">
                      <HiChartBar className="w-6 h-6 text-orange-600" />
                    </div>
                    <Typography variant="h6" className="font-bold text-gray-900" sx={{ fontSize: "1.25rem" }}>
                      Dimensions & Weight
                    </Typography>
                  </div>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-gray-600 font-medium">Height</span>
                      <span className="font-semibold text-gray-900">{newBike.height}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-gray-600 font-medium">Length</span>
                      <span className="font-semibold text-gray-900">{newBike.length}</span>
                    </div>
                    {newBike.kerbWeightInKg && (
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-gray-600 font-medium">Kerb Weight</span>
                        <span className="font-semibold text-gray-900">{newBike.kerbWeightInKg} KG</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center py-2">
                      <span className="text-gray-600 font-medium">ABS</span>
                      <Chip 
                        label={newBike.abs} 
                        className={newBike.abs === "Yes" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"} 
                        size="small" 
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card className="bg-white rounded-2xl shadow-lg border-0" sx={{ borderRadius: "16px" }}>
              <CardContent className="p-6">
                <div className="flex items-center mb-4">
                  <div className="p-2 bg-indigo-100 rounded-lg mr-3">
                      <HiBolt className="w-6 h-6 text-indigo-600" />
                  </div>
                  <Typography variant="h6" className="font-bold text-gray-900" sx={{ fontSize: "1.25rem" }}>
                    Additional Features
                  </Typography>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {newBike.breakType && (
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-gray-600 font-medium">Brake Type</span>
                      <span className="font-semibold text-gray-900">{newBike.breakType}</span>
                    </div>
                  )}
                  {newBike.headLight && (
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-gray-600 font-medium">Head Light</span>
                      <span className="font-semibold text-gray-900">{newBike.headLight}</span>
                    </div>
                  )}
                  {newBike.tailLight && (
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-gray-600 font-medium">Tail Light</span>
                      <span className="font-semibold text-gray-900">{newBike.tailLight}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center py-2">
                    <span className="text-gray-600 font-medium">Launch Date</span>
                    <span className="font-semibold text-gray-900">{newBike.launchDate}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        );

      case 2:
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Card className="bg-white rounded-2xl shadow-lg border-0" sx={{ borderRadius: "16px" }}>
              <CardContent className="p-6">
                <div className="flex items-center mb-6">
                  <div className="p-2 bg-blue-100 rounded-lg mr-3">
                    <HiWrenchScrewdriver className="w-6 h-6 text-blue-600" />
                  </div>
                  <Typography variant="h6" className="font-bold text-gray-900" sx={{ fontSize: "1.25rem" }}>
                    Service Schedule
                  </Typography>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex justify-between items-center py-3 px-4 bg-gray-50 rounded-xl">
                      <span className="text-gray-600 font-medium">Service 1</span>
                      <span className="font-bold text-primary-600">{newBike.service1} kms</span>
                    </div>
                    <div className="flex justify-between items-center py-3 px-4 bg-gray-50 rounded-xl">
                      <span className="text-gray-600 font-medium">Service 2</span>
                      <span className="font-bold text-primary-600">{newBike.service2} kms</span>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center py-3 px-4 bg-gray-50 rounded-xl">
                      <span className="text-gray-600 font-medium">Service 3</span>
                      <span className="font-bold text-primary-600">{newBike.service3} kms</span>
                    </div>
                    <div className="flex justify-between items-center py-3 px-4 bg-gray-50 rounded-xl">
                      <span className="text-gray-600 font-medium">Service 4</span>
                      <span className="font-bold text-primary-600">{newBike.service4} kms</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        );

      case 3:
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Card className="bg-white rounded-2xl shadow-lg border-0" sx={{ borderRadius: "16px" }}>
              <CardContent className="p-6">
                <div className="flex items-center mb-6">
                  <div className="p-2 bg-green-100 rounded-lg mr-3">
                    <HiBattery100 className="w-6 h-6 text-green-600" />
                  </div>
                  <Typography variant="h6" className="font-bold text-gray-900" sx={{ fontSize: "1.25rem" }}>
                    Battery Information
                  </Typography>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex justify-between items-center py-3 px-4 bg-green-50 rounded-xl">
                      <span className="text-gray-600 font-medium">Charging Time</span>
                      <span className="font-bold text-green-600">{newBike.chargingTimeInHours} Hours</span>
                    </div>
                    <div className="flex justify-between items-center py-3 px-4 bg-green-50 rounded-xl">
                      <span className="text-gray-600 font-medium">Max Power</span>
                      <span className="font-bold text-green-600">{newBike.maxPowerInKw} KW</span>
                    </div>
                    <div className="flex justify-between items-center py-3 px-4 bg-green-50 rounded-xl">
                      <span className="text-gray-600 font-medium">Fast Charging</span>
                      <Chip 
                        label={newBike.fastCharging} 
                        className={newBike.fastCharging === "Yes" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"} 
                        size="small" 
                      />
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center py-3 px-4 bg-green-50 rounded-xl">
                      <span className="text-gray-600 font-medium">Battery Capacity</span>
                      <span className="font-bold text-green-600">{newBike.batteryCapacityInKwh} KWh</span>
                    </div>
                    <div className="flex justify-between items-center py-3 px-4 bg-green-50 rounded-xl">
                      <span className="text-gray-600 font-medium">Battery Warranty</span>
                      <span className="font-bold text-green-600">{newBike.batteryWarranty}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <div className="w-16 h-16 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mx-auto mb-4"></div>
          <Typography variant="h6" className="text-gray-600">
            Loading bike details...
          </Typography>
        </motion.div>
      </div>
    );
  }

  if (!newBike) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <HiSparkles className="w-12 h-12 text-gray-400" />
          </div>
          <Typography variant="h5" className="text-gray-500 mb-3 font-semibold">
            Bike not found
          </Typography>
          <Typography variant="body1" className="text-gray-400 mb-6">
            The bike you're looking for doesn't exist or has been removed.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate("/new-bike-list-router")}
            className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white rounded-xl px-6 py-3 font-semibold"
            startIcon={<HiArrowLeft className="w-5 h-5" />}
          >
            Back to New Bikes
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Compact Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-gradient-to-r from-primary-600 to-accent-600 text-white py-2 px-4"
      >
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Button
            variant="outlined"
            onClick={() => navigate("/new-bike-list-router")}
            className="border-white text-white hover:bg-white hover:text-primary-600 rounded-lg px-3 py-1"
            startIcon={<HiArrowLeft className="w-4 h-4" />}
          >
            Back
          </Button>
          <div className="text-center">
            <Typography variant="h6" className="font-bold" sx={{ fontSize: "1.25rem" }}>
              {newBike.brand} {newBike.model}
            </Typography>
            <Typography variant="body2" className="opacity-90" sx={{ fontSize: "0.875rem" }}>
              {newBike.variant} • {newBike.color}
            </Typography>
          </div>
          <Button
            variant="outlined"
            className="border-white text-white hover:bg-white hover:text-primary-600 rounded-lg px-3 py-1"
            startIcon={<HiHeart className="w-4 h-4" />}
          >
            Save
          </Button>
        </div>
      </motion.div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Image Carousel */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="mb-4"
        >
          <Card className="bg-white rounded-2xl shadow-xl border-0 overflow-hidden" sx={{ borderRadius: "16px" }}>
            <MainCarousel imageList={images} />
          </Card>
        </motion.div>

        {/* Price Card - Label Above Price */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="mb-6"
        >
          <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl shadow-lg border-0" sx={{ borderRadius: "12px" }}>
            <CardContent className="px-4 py-1 text-center">
              <Typography variant="body2" className="opacity-80 mb-0.5" sx={{ fontSize: "0.7rem" }}>
                Ex-Showroom Price
              </Typography>
              <div className="flex items-center justify-center">
                <Typography variant="h5" className="font-bold" sx={{ fontSize: "1.5rem" }}>
                  ₹{newBike.exShowroomPrice?.toLocaleString()}
                </Typography>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Custom Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="mb-8"
        >
          <Card className="bg-white rounded-2xl shadow-lg border-0" sx={{ borderRadius: "16px" }}>
            <CardContent className="p-6">
              <div className="flex gap-1 mb-6 overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent" style={{ scrollbarWidth: 'thin' }}>
                {tabs.slice(0, newBike.fuelType === "ELECTRIC" ? 4 : 3).map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-1 px-3 py-2 rounded-lg font-medium transition-all duration-300 whitespace-nowrap text-sm ${
                      activeTab === tab.id
                        ? "bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-lg"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    {tab.icon}
                    {tab.label}
                  </button>
                ))}
              </div>
              
              <AnimatePresence mode="wait">
                {renderTabContent()}
              </AnimatePresence>
            </CardContent>
          </Card>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.6 }}
          className="flex flex-col sm:flex-row gap-4 justify-center"
        >
          <Button
            variant="contained"
            size="large"
            className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white rounded-xl px-8 py-4 font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
            startIcon={<HiEye className="w-5 h-5" />}
          >
            Contact Dealer
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate("/new-bike-list-router")}
            className="border-2 border-primary-500 text-primary-600 hover:bg-primary-500 hover:text-white rounded-xl px-8 py-4 font-semibold transition-all duration-300"
            startIcon={<HiArrowLeft className="w-5 h-5" />}
          >
            Back to Listings
          </Button>
        </motion.div>
      </div>
    </div>
  );
}
